<template>
  <div class="homepage-container">
    <!-- Section 1: Stock Up & Save Split Section -->
    <section class="stock-up-save-section">
      <div class="container hero-container">
        <div class="row align-items-center">
          <div class="col-lg-6 col-12 py-3 hero-left-col">
            <div class="hero-left-content">
              <h1 class="hero-title animate-slide-up">
                <span class="text-blue">{{ $t('Home.StockUpTitle') }}</span>
                <span class="text-black">{{ $t('Home.AndSaveTitle') }}</span>
              </h1>
              <p class="hero-desc animate-slide-up delay-1">
                {{ $t('Home.StockUpDesc') }}
              </p>
              
              <div class="barcode-card animate-slide-up delay-2">
                <h2 class="barcode-card-title">{{ $t('Home.GetYourBarcodeTitle') }}</h2>
                <p class="barcode-card-desc">{{ $t('Home.GetYourBarcodeDesc') }}</p>
                
                <div class="barcode-btn-container">
                  <router-link to="/SignIn" class="btn-primary-get-started">
                    {{ $t('Home.GetStartedBtn') }}
                  </router-link>
                </div>
                
                <div class="barcode-card-links">
                  <div class="link-item">
                    View all <a href="http://snipp.us/!cuRVc" target="_blank" class="accent-link">participating products and offers</a>
                  </div>
                  <div class="link-item">
                    Trouble participating? Check our <router-link to="/FAQ" class="accent-link">FAQs</router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Desktop right-side banner image -->
      <div class="hero-right-image-wrapper d-none d-lg-block">
        <img src="/assets/images/offer-wall-banner.png" alt="Offer Wall Banner" />
      </div>
    </section>

    <!-- Section 3: How It Works Section -->
    <section class="how-it-works" :style="{ '--htw-bg-desktop': 'url(' + $t('Home.HtwBgDesktop') + ')', '--htw-bg-mobile': 'url(' + $t('Home.HtwBgMobile') + ')' }">
      <div class="container htw-container">
        <div class="row align-items-center">
          <div class="col-lg-4 col-12 htw-left-col">
            <div class="htw-left-content animate-fade-in">
              <h2 class="htw-heading-left">{{ $t('Home.HtwTitleDesktop') }}</h2>
              <p class="htw-desc-left">
                {{ $t('Home.HtwDesc') }}
              </p>
            </div>
          </div>
          <div class="col-lg-8 col-12 mt-4 mt-lg-0">
            <div class="htw-cards-grid">
              <!-- Card 1 -->
              <div class="htw-card animate-card-delay-1">
                <div class="card-icon-container">
                  <img src="/assets/images/Tag.png" alt="Snipp Offers Icon" />
                </div>
                <h3 class="card-title">{{ $t('Home.HtwStep1Title') }}</h3>
                <p class="card-desc">{{ $t('Home.HtwStep1Desc') }}</p>
              </div>

              <!-- Card 2 -->
              <div class="htw-card animate-card-delay-2">
                <div class="card-icon-container">
                  <img src="/assets/images/Barcode.png" alt="Get Barcode Icon" />
                </div>
                <h3 class="card-title">{{ $t('Home.HtwStep2Title') }}</h3>
                <p class="card-desc">{{ $t('Home.HtwStep2Desc') }}</p>
              </div>

              <!-- Card 3 -->
              <div class="htw-card animate-card-delay-3">
                <div class="card-icon-container">
                  <img src="/assets/images/cart.png" alt="Shop & Save Icon" />
                </div>
                <h3 class="card-title">{{ $t('Home.HtwStep3Title') }}</h3>
                <p class="card-desc">{{ $t('Home.HtwStep3Desc') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 4: Save On Your Favorites -->
    <section class="save-favorites-section">
      <div class="container">
        <div class="row">
          <!-- Desktop left details -->
          <div class="col-lg-4 col-12 fav-left-col">
            <div class="fav-left-content animate-slide-up">
              <h2 class="fav-title">{{ $t('Home.SaveFavoritesTitle') }}</h2>
              <p class="fav-desc">
                {{ $t('Home.SaveFavoritesDesc') }}
              </p>
              <router-link to="/SignIn" class="btn-outline-offers d-none d-lg-inline-block">
                {{ $t('Home.ViewAllOffersBtn') }}
              </router-link>
            </div>
          </div>
          <!-- Desktop right grid & Mobile slider -->
          <div class="col-lg-8 col-12 mt-4 mt-lg-0">
            <!-- Desktop Grid -->
            <div class="offers-grid">
              <div class="offer-card animate-slide-up" v-for="(offer, idx) in offers" :key="'grid-'+offer.id" :class="'delay-' + (idx % 3)">
                <div class="offer-badge">{{ $t('Home.SaveBadgeText') }}</div>
                <div class="offer-img-container">
                  <img :src="offer.image" alt="Product Offer" />
                </div>
                <div class="offer-info-bar">
                  <h4>{{ $t('Home.BrandName') }}</h4>
                  <p>{{ $t('Home.ProductOfferDesc') }}</p>
                </div>
              </div>
            </div>

            <!-- Mobile/Tablet Slider -->
            <div class="offers-slider-wrapper">
              <div class="offers-slider-container" ref="carouselContainer">
                <div class="offer-card" v-for="offer in offers" :key="'slide-'+offer.id">
                  <div class="offer-badge">{{ $t('Home.SaveBadgeText') }}</div>
                  <div class="offer-img-container">
                    <img :src="offer.image" alt="Product Offer" />
                  </div>
                  <div class="offer-info-bar">
                    <h4>{{ $t('Home.BrandName') }}</h4>
                    <p>{{ $t('Home.ProductOfferDesc') }}</p>
                  </div>
                </div>
              </div>
              <!-- Controls -->
              <div class="offers-slider-controls">
                <router-link to="/SignIn" class="btn-outline-offers">
                  {{ $t('Home.ViewAllOffersBtn') }}
                </router-link>
                <div class="slider-arrows">
                  <button class="arrow-btn" @click="scrollLeft" aria-label="Previous Offer">
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <button class="arrow-btn" @click="scrollRight" aria-label="Next Offer">
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  props: {
    startDate: {
      type: Date,
      required: true,
    },
    endDate: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      offers: [
        { id: 1, image: '/assets/images/offer-img1.png' },
        { id: 2, image: '/assets/images/offer-img2.png' },
        { id: 3, image: '/assets/images/offer-img3.png' },
        { id: 4, image: '/assets/images/offer-img2.png' },
        { id: 5, image: '/assets/images/offer-img4.png' },
        { id: 6, image: '/assets/images/offer-img3.png' }
      ]
    };
  },
  methods: {
    scrollLeft() {
      const carousel = this.$refs.carouselContainer;
      if (carousel) {
        const card = carousel.querySelector('.offer-card');
        const cardWidth = card ? card.offsetWidth + 20 : 300;
        carousel.scrollBy({ left: -cardWidth, behavior: 'smooth' });
      }
    },
    scrollRight() {
      const carousel = this.$refs.carouselContainer;
      if (carousel) {
        const card = carousel.querySelector('.offer-card');
        const cardWidth = card ? card.offsetWidth + 20 : 300;
        carousel.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
/* SECTION A: Stock Up & Save */
.stock-up-save-section {
  position: relative;
  background-color: #ffffff;
  min-height: 620px;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 4rem 0;
}

.hero-left-col {
  position: relative;
  z-index: 2;
}

.hero-left-content {
  max-width: 540px;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 1.5rem;
}

.hero-title .text-blue {
  color: #2584C5;
}

.hero-title .text-black {
  color: #000000;
}

.hero-desc {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #333333;
  margin-bottom: 2.5rem;
}

/* Barcode Card */
.barcode-card {
  background: #ffffff;
  border: 1px solid #eaeef2;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  text-align: left;
}

.barcode-card-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
}

.barcode-card-desc {
  font-size: 1rem;
  color: #555555;
  margin-bottom: 1.75rem;
  line-height: 1.5;
}

.barcode-btn-container {
  margin-bottom: 1.5rem;
}

.btn-primary-get-started {
  display: inline-block;
  background-color: #2584C5;
  color: #ffffff !important;
  font-size: 1.05rem;
  font-weight: 600;
  padding: 0.75rem 4.5rem;
  border-radius: 8px;
  text-decoration: none;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 4px 12px rgba(37, 132, 197, 0.15);
}

.btn-primary-get-started:hover {
  background-color: #1b75bb;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(37, 132, 197, 0.3);
  text-decoration: none !important;
}

.btn-primary-get-started:active {
  transform: translateY(0);
}

.barcode-card-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.link-item {
  font-size: 0.9rem;
  color: #666666;
}

.accent-link {
  color: #2584C5 !important;
  text-decoration: underline !important;
  font-weight: 500;
}

.accent-link:hover {
  color: #1b75bb !important;
}

/* Hero Right Image */
.hero-right-image-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  z-index: 1;
}

.hero-right-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* How It Works Section */
.how-it-works {
  background-image: var(--htw-bg-desktop);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  padding: 6rem 0;
  position: relative;
}

.htw-container {
  max-width: 1200px;
  margin: 0 auto;
}

.htw-left-col {
  padding-right: 2.5rem;
}

.htw-heading-left {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1.5rem;
  line-height: 1.25;
}

.htw-desc-left {
  font-size: 1.05rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0;
}

.htw-cards-grid {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 1.5rem;
}

.htw-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 2.5rem 1.75rem;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.htw-card:hover {
  transform: translateY(-8px);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  background: rgba(255, 255, 255, 0.12);
}

.card-icon-container {
  background: #ffffff;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.card-icon-container img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #ffffff;
}

.card-desc {
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
}

/* Animations */
.animate-fade-in {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
}

.delay-1 {
  animation-delay: 0.2s;
}

.delay-2 {
  animation-delay: 0.4s;
}

.animate-slide-left {
  opacity: 0;
  transform: translateY(-50%) translateX(-30px);
  animation: slideLeft 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.3s;
}

.animate-slide-right {
  opacity: 0;
  transform: translateY(-50%) translateX(30px);
  animation: slideRight 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.3s;
}

.animate-card-delay-1 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.4s;
}

.animate-card-delay-2 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.6s;
}

.animate-card-delay-3 {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.8s;
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeft {
  to {
    opacity: 0.85;
    transform: translateY(-50%) translateX(0);
  }
}

@keyframes slideRight {
  to {
    opacity: 0.85;
    transform: translateY(-50%) translateX(0);
  }
}

/* Responsive Design */
@media screen and (max-width: 1199px) {
  .hero-title {
    font-size: 3rem;
  }
  .barcode-card {
    padding: 2rem;
  }
  .htw-heading-left {
    font-size: 2.25rem;
  }
}

@media screen and (max-width: 991px) {
  .stock-up-save-section {
    padding: 3rem 0;
    min-height: auto;
  }
  .hero-left-content {
    max-width: 100%;
    text-align: center;
    margin: 0 auto;
  }
  .hero-title {
    font-size: 2.75rem;
  }
  .barcode-card {
    margin: 2rem auto 0 auto;
  }

  .htw-left-col {
    text-align: center;
    padding-right: 0;
    margin-bottom: 2.5rem;
  }
  
  .htw-cards-grid {
    flex-direction: column;
    gap: 1.5rem;
    padding: 0;
    max-width: 500px;
    margin: 0 auto;
  }
  
  .htw-card {
    padding: 2.5rem 2rem;
  }
  
  .how-it-works {
    padding: 4.5rem 0;
    background-image: var(--htw-bg-mobile) !important;
  }
}

@media screen and (max-width: 767px) {
  .hero-title {
    font-size: 2.25rem;
  }
  .barcode-card {
    padding: 1.5rem;
  }
  .barcode-card-title {
    font-size: 1.5rem;
  }
  .btn-primary-get-started {
    width: 100%;
    padding: 0.75rem 2rem;
  }
  
  .how-it-works {
    padding: 3.5rem 0;
  }
  
  .htw-heading-left {
    font-size: 1.85rem;
    margin-bottom: 1.5rem;
  }
}

/* SECTION C: Save On Your Favorites */
.save-favorites-section {
  background-color: #ffffff;
  padding: 6rem 0;
}

.fav-left-content {
  padding-right: 2rem;
}

.fav-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.fav-desc {
  font-size: 1.05rem;
  line-height: 1.6;
  color: #555555;
  margin-bottom: 2.5rem;
}

.btn-outline-offers {
  display: inline-block;
  background-color: #ffffff;
  color: #2584C5 !important;
  border: 1px solid #2584C5;
  font-size: 1.05rem;
  font-weight: 600;
  padding: 0.75rem 2.25rem;
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-outline-offers:hover {
  background-color: #2584C5;
  color: #ffffff !important;
  text-decoration: none !important;
  box-shadow: 0 4px 12px rgba(37, 132, 197, 0.2);
}

.offers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.offer-card {
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  display: flex;
  flex-direction: column;
}

.offer-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.offer-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: #2584C5;
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  z-index: 3;
}

.offer-img-container {
  height: 230px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
}

.offer-img-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.offer-card:hover .offer-img-container img {
  transform: scale(1.05);
}

.offer-info-bar {
  background-color: #5ea7db;
  padding: 1.25rem 1.5rem;
  color: #ffffff;
  margin-top: auto;
}

.offer-info-bar h4 {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #ffffff;
}

.offer-info-bar p {
  font-size: 0.9rem;
  opacity: 0.95;
  margin-bottom: 0;
  color: #ffffff;
}

/* Mobile specific slider container styles */
.offers-slider-wrapper {
  display: none;
}

/* RESPONSIVE OVERRIDES FOR SECTION C */
@media screen and (max-width: 1199px) {
  .offers-grid {
    gap: 1rem;
  }
  .offer-img-container {
    height: 200px;
  }
}

@media screen and (max-width: 991px) {
  .save-favorites-section {
    padding: 4rem 0;
  }
  .fav-left-col {
    text-align: center;
    padding-right: 0;
    margin-bottom: 2.5rem;
  }
  .offers-grid {
    display: none; /* Hide grid and show slider on mobile/tablet */
  }
  
  /* Mobile slider controls & swiping */
  .offers-slider-wrapper {
    display: block;
    width: 100%;
  }
  
  .offers-slider-container {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 1.25rem;
    padding: 0.5rem 0.25rem 1.5rem 0.25rem;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  
  .offers-slider-container::-webkit-scrollbar {
    display: none; /* Hide scrollbars */
  }
  
  .offers-slider-container .offer-card {
    flex: 0 0 280px;
    scroll-snap-align: start;
  }
  
  .offers-slider-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.5rem;
    padding: 0 0.5rem;
  }
  
  .slider-arrows {
    display: flex;
    gap: 0.75rem;
  }
  
  .arrow-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid #2584C5;
    background: transparent;
    color: #2584C5;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.1rem;
    padding: 0;
  }
  
  .arrow-btn:hover {
    background-color: #2584C5;
    color: #ffffff;
  }
  
  .arrow-btn:active {
    transform: scale(0.95);
  }
}

@media screen and (max-width: 575px) {
  .fav-title {
    font-size: 2rem;
  }
  .arrow-btn {
    width: 40px;
    height: 40px;
  }
}
</style>
